// ─── Feuille : saisir / éditer une OPÉRATION ──────────────────────
// Dépense OU revenu (`sens`), payée ou en PROVISION (`paye:false`).
// La date passe par le sélecteur du Core, rendu en modale SŒUR au niveau de
// l'écran (`onDemanderDate`) : deux <Modal> natives imbriquées figent l'app.
import React, { useEffect, useMemo, useState } from 'react';
import { View, Text, TextInput, TouchableOpacity, StyleSheet } from 'react-native';
import { Feuille, ChoixPuces, Bascule, stylesForm, frJMA, useUI, useT, SelecteurListe } from '@mop/core';
import { TYPES_PAIEMENT, emojiCategorie, cleCategorie, clePaiement } from './data/categories';
import { categoriesActives, sousCategoriesDe } from './services/categoriesPerso';

const aujourdhui = () => new Date().toISOString().slice(0, 10);
const VIDE = {
  sens: 'depense', montant: '', payeurUid: '', categorie: 'autre', libelle: '',
  date: '', datePaiement: '', typePaiement: '', commentaire: '', paye: true, sousCategorie: '',
};

export default function FeuilleOperation({ visible, initial, membres = [], monUid, onDemanderDate, onEnregistrer, onFermer, configCategories }) {
  const UI = useUI();
  const t = useT();
  const s = useMemo(() => makeStyles(UI), [UI]);
  const [f, setF] = useState(VIDE);

  useEffect(() => {
    if (!visible) return;
    const o = initial || {};
    setF({
      ...VIDE, ...o,
      montant: o.montant != null ? String(o.montant) : '',
      payeurUid: o.payeurUid || monUid || '',
      date: o.date || aujourdhui(),
      paye: o.paye !== false,
    });
  }, [visible, initial, monUid]);

  const set = (k, v) => setF(x => ({ ...x, [k]: v }));
  const montantNum = parseFloat(String(f.montant).replace(',', '.')) || 0;
  const valide = montantNum > 0;

  const LigneDate = ({ cle, valeur, onEffacer }) => (
    <TouchableOpacity style={s.date} activeOpacity={0.7}
      onPress={() => onDemanderDate?.({ valeur, onChoisir: (dk) => set(cle, dk) })}>
      <Text style={{ fontSize: 15 }}>📅</Text>
      <Text style={[s.dateTxt, !valeur && { color: UI.faint }]}>
        {valeur ? frJMA(valeur) : t('commun.choisirDate')}
      </Text>
      {!!valeur && onEffacer && (
        <TouchableOpacity onPress={onEffacer} hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}>
          <Text style={{ color: UI.muted, fontSize: 16 }}>✕</Text>
        </TouchableOpacity>
      )}
    </TouchableOpacity>
  );

  return (
    <Feuille visible={visible} titre={initial?.id ? t('commun.modifier') : t('budget.depense')}
      emoji={f.sens === 'revenu' ? '💶' : '🧾'} hauteur={0.85} onFermer={onFermer}
      action={{ icone: initial?.id ? '💾' : '＋', label: t('commun.enregistrer'), disabled: !valide,
        onPress: () => onEnregistrer({ ...f, montant: montantNum, sousCategorie: f.sousCategorie || null }) }}>

      <ChoixPuces
        options={[{ id: 'depense', label: `🧾 ${t('budget.depense')}` }, { id: 'revenu', label: `💶 ${t('budget.revenus')}` }]}
        value={f.sens} onChange={v => set('sens', v)} />

      <Text style={s.lbl}>{t('budget.montant')}</Text>
      <TextInput style={[s.input, s.montant]} value={f.montant} onChangeText={v => set('montant', v.replace(/[^0-9.,]/g, ''))}
        keyboardType="decimal-pad" placeholder="0,00" placeholderTextColor={UI.faint} />

      <Text style={s.lbl}>{t('budget.libelle')}</Text>
      <TextInput style={s.input} value={f.libelle} onChangeText={v => set('libelle', v)}
        placeholder={t('budget.libellePlaceholder')} placeholderTextColor={UI.faint} />

      <Text style={s.lbl}>{t('budget.payePar')}</Text>
      {membres.length === 0 ? (
        <Text style={s.vide}>{t('budget.aucunMembreDispo')}</Text>
      ) : (
        <ChoixPuces options={membres.map(m => ({ id: m.uid, label: m.nom }))}
          value={f.payeurUid} onChange={v => set('payeurUid', v)} />
      )}

      <Text style={s.lbl}>{t('budget.categorie')}</Text>
      {/* Liste deroulante (convention du 11/08) alimentee par les categories
          ACTIVES : defauts non masques + personnalisees (Cedric, 15/08). */}
      <SelecteurListe titre={t('budget.categorie')} emoji="🏷️"
        options={categoriesActives(configCategories).map(c => ({ id: c.id, label: c.nom || t(cleCategorie(c.id)), emoji: c.emoji }))}
        valeur={f.categorie} onChange={v => setF(x => ({ ...x, categorie: v, sousCategorie: '' }))} />

      {/* Sous-categorie, entierement optionnelle — visible seulement si la
          categorie en a. */}
      {sousCategoriesDe(f.categorie, configCategories).length > 0 && (
        <>
          <Text style={s.lbl}>{t('finances.sousCategorie')}</Text>
          <SelecteurListe titre={t('finances.sousCatOptionnelle')} emoji="🏷️"
            avecVide libelleVide={t('commun.aucune')}
            options={sousCategoriesDe(f.categorie, configCategories).map(x => ({ id: x, label: x }))}
            valeur={f.sousCategorie} onChange={v => set('sousCategorie', v || '')} />
        </>
      )}

      <Text style={s.lbl}>{t('budget.date')}</Text>
      <LigneDate cle="date" valeur={f.date} />

      {/* Une PROVISION est prévue mais pas encore réglée : elle ne compte ni
          dans le réalisé ni dans les dettes, seulement dans l'estimé. */}
      <View style={s.bascule}>
        <Text style={s.basculeTxt}>{f.paye ? t('budget.statutPaye') : `⏳ ${t('budget.provision')}`}</Text>
        <Bascule valeur={f.paye} onChange={v => set('paye', v)} />
      </View>

      {f.paye && (
        <>
          <Text style={s.lbl}>{t('budget.datePaiement')}</Text>
          <LigneDate cle="datePaiement" valeur={f.datePaiement} onEffacer={() => set('datePaiement', '')} />
          <Text style={s.lbl}>{t('budget.typePaiement')}</Text>
          <ChoixPuces options={TYPES_PAIEMENT.map(p => ({ id: p.id, label: `${p.emoji} ${t(clePaiement(p.id))}` }))}
            value={f.typePaiement} onChange={v => set('typePaiement', v === f.typePaiement ? '' : v)} />
        </>
      )}

      <Text style={s.lbl}>{t('budget.commentaire')}</Text>
      <TextInput style={[s.input, { minHeight: 60 }]} value={f.commentaire} onChangeText={v => set('commentaire', v)}
        multiline placeholder="…" placeholderTextColor={UI.faint} />
    </Feuille>
  );
}

const makeStyles = (UI) => StyleSheet.create({
  ...stylesForm(UI),
  montant: { fontSize: 22, fontWeight: '800', textAlign: 'right' },
  date: { flexDirection: 'row', alignItems: 'center', gap: 10, backgroundColor: UI.card, borderRadius: 11, borderWidth: 0.5, borderColor: UI.border, paddingVertical: 12, paddingHorizontal: 12 },
  dateTxt: { flex: 1, fontSize: 14.5, color: UI.ink, fontWeight: '600' },
  bascule: { flexDirection: 'row', alignItems: 'center', gap: 12, marginTop: 14, backgroundColor: UI.card, borderRadius: 12, borderWidth: 0.5, borderColor: UI.border, padding: 12 },
  basculeTxt: { flex: 1, fontSize: 14.5, fontWeight: '700', color: UI.ink },
  vide: { fontSize: 13, color: UI.faint, paddingVertical: 8 },
});
